/**
 * Potter's Vineyard variant — UI overrides + extras for tenant `pottersvineyard`.
 *
 * Entry point invoked by loadVariant() (src/variants/index.js) when the widget
 * loads for this tenant. Ported feature-for-feature from Lightning Rock
 * Winery's index.js — both are Commerce7 tenants on the same "reviewed"
 * checkout flow (add to cart, ship/pickup options, order summary, saved
 * shipping addresses, saved payment cards, order confirmation, account
 * login). Only tenant-specific values differ (contact copy, guided flow,
 * country/ZIP handling in CheckoutForm.js) — colors are NOT hardcoded here;
 * they come from Potter's own tenant-config theme keys (see styles.css).
 *
 * REGISTERED EXTRAS:
 *   - WineCard — in-widget Commerce7 add-to-cart card (old createWineListing)
 *   - MembershipCard — Join Now / no bullets
 *   - cart + checkout when commerce7 / show_cart
 *   - account greeting + in-chat login when show_user_account
 *   - contact-options
 */

import { on, emit, addMessage, getState, setState } from "../../core/state.js";
import { getContact, getFeature, getUiFeature } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import PVWineCard from "./components/WineCard.js";
import PVMembershipCard from "./components/MembershipCard.js";
import PVCheckoutForm from "./components/CheckoutForm.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import * as cartFeature from "../../features/optional/cart_c7.js";
import * as checkoutFeature from "../../features/optional/checkout_c7.js";
import * as accountFeature from "../../features/optional/account.js";
import PVOrderReview from "./components/OrderReview.js";
import PVOrderConfirmation from "./components/OrderConfirmation.js";
import PVLoginForm from "./components/LoginForm.js";
import PVHeader from "./components/Header.js";
import PVInputBar from "./components/InputBar.js";
import PVCheckoutOptions, { initDeliveryLoading } from "./components/CheckoutOptions.js";
import PVCartPanel from "./components/CartPanel.js";
import { initCheckoutRefresh } from "./checkoutRefresh.js";

// Parity with old showDefaultOptions: after a contact follow-up, re-prompt
// with "Is there anything else…" + the guided buttons.
function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  addMessage({ role: "bot", kind: "prompts", data: { items: getState().tenant.guided_flow_questions } });
}
function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

// Keep the configured price ("$50 per person") on its own banner line, as in
// the Goosecross banner. No-op when the text has no trailing price.
function formatBannerText(text) {
  if (typeof text !== "string") return text;
  return text.replace(/\s+(\$\s*\d[\s\S]*)$/, "\n$1");
}

// tel: href from a display phone (strip spaces/parens, keep +digits).
function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

export async function register() {
  registerMessageKind("contact-options", ContactOptions);

  // Override the base MembershipCard — the simple card (image, uppercase
  // title, detailed_description, Learn more, Join Now) with no type/meta/price.
  // Runs after registerBaseCards (last wins). See the component.
  registry.registerComponent("MembershipCard", PVMembershipCard);

  // Old prod account control: outlined person glyph + icon-tile dropdown rows.
  registry.registerComponent("Header", PVHeader);
  registry.registerComponent("InputBar", PVInputBar);

  // Parity: old VISIT US promo (.promo-text-right-featured) called
  // sendMessage("Show me all tasting experiences"). Tenant config often only
  // has banner_cta_label / banner_cta_url — seed the chat query so Banner.js
  // fires get_response_type instead of doing nothing / opening a link.
  // Skip when banner_cta_url is set (e.g. an event "PURCHASE TICKET" link) —
  // Banner.js prefers the query, so seeding it would swallow the URL.
  const t = getState().tenant || {};
  if (t.theme && !t.theme.banner_cta_query && !t.theme.banner_cta_url && !t.theme.banner?.cta_url) {
    setState({
      tenant: {
        ...t,
        theme: {
          ...t.theme,
          banner_cta_query: "Show me all tasting experiences",
        },
      },
    });
  }

  // Event promo: put the configured price on its own line (Goosecross parity).
  const cfg = getState().tenant || {};
  // An event CTA URL (e.g. PURCHASE TICKET) wins over the VISIT US chat query —
  // Banner.js runs the query first, which would swallow the link.
  const ctaUrlSet = Boolean(cfg.theme?.banner_cta_url || cfg.theme?.banner?.cta_url);
  const theme = cfg.theme && {
    ...cfg.theme,
    ...(ctaUrlSet && { banner_cta_query: null }),
    banner_text: formatBannerText(cfg.theme.banner_text),
    banner_text_alt: formatBannerText(cfg.theme.banner_text_alt),
    ...(cfg.theme.banner && {
      banner: {
        ...cfg.theme.banner,
        text: formatBannerText(cfg.theme.banner.text),
        text_alt: formatBannerText(cfg.theme.banner.text_alt),
        ...(ctaUrlSet && { cta_query: null }),
      },
    }),
  };
  setState({ tenant: { ...cfg, theme, guided_flow_questions: cfg.guided_flow_questions } });

  // Contact our team — a bot question + the connect options (Email Us / Call
  // Us — no Text a Live Person on Commerce7 tenants, RedChirp isn't wired up
  // for them). No login prompt here; that only shows on open (account.js).
  on("ws-prompt-select", ({ value }) => {
    if (value !== "__contact__") return;
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "How would you like to connect with our team?" },
    });
    addMessage({
      role: "bot",
      kind: "contact-options",
      data: { email: getContact("email"), phone: getContact("phone"), text_live_person: false },
    });
  });

  // Call Us → follow-up + re-prompt (old showContactNumber).
  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `You can call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  // Email Us → follow-up + re-prompt (old showContactEmailMessage). The mailto
  // itself is opened by the ContactOptions link.
  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  // Always use Potter's WineCard so Shop Our Wines matches createWineListing
  // (even when cart flags are off — otherwise the base rich card loads instead).
  registry.registerComponent("WineCard", PVWineCard);

  // Commerce7 cart — enabled when either show_cart or commerce7 is on in
  // tenant config.
  if (getUiFeature("show_cart", false) || getFeature("commerce7")) {
    cartFeature.init({ minimumLoadingMs: 2000 });
    // Full in-widget Commerce7 checkout flow (owns ws-checkout): checkout
    // options → ship/pickup form → order review → payment → confirmation.
    // checkout_c7.js is shared with Valle della Pace; the "reviewed" flow
    // opts into this tenant's own step sequence (see its header doc).
    checkoutFeature.init({ flow: "reviewed" });
    registerMessageKind("checkout-options", PVCheckoutOptions);
    initDeliveryLoading();
    // Old prod Proceed to Checkout: after the Shipping/Pickup options post,
    // show "Updating your cart..." and reload with ?openagent=true. Must be
    // registered AFTER checkoutFeature.init so the options message exists.
    initCheckoutRefresh();
    // Re-prompt with the guided buttons once the order is placed (parity with
    // the contact-flow follow-ups above).
    on("ws-order-confirmed", () => {
      postDefaultOptions();
    });
    // Potter's ships C7 (no Shopify company/state/phone) — override the base
    // checkout-form renderer, which otherwise renders for every C7 tenant.
    registerMessageKind("checkout-form", PVCheckoutForm);
    // Keep the beginning of a newly opened delivery form visible while its
    // country fields settle; cancel any earlier bottom-scroll settling loop.
    on("ws-message-added", (message) => {
      if (message.kind !== "checkout-form" ||
          !["ship", "pickup"].includes(message.data?.type)) return;
      requestAnimationFrame(() => {
        emit("ws-pin-scroll-to", {
          selector: `[data-pv-checkout-id="${CSS.escape(message.id)}"]`,
          align: "start",
        });
      });
    });
    registerMessageKind("order-review", PVOrderReview);
    registerMessageKind("order-confirmation", PVOrderConfirmation);
    // Old prod order summary copy (volume meta, no deposit row, "Proceed to
    // Checkout"); layout in styles.css.
    registerMessageKind("cart", PVCartPanel);
  }

  // Account greeting / login prompt is gated on the account UI toggle ONLY —
  // NOT on commerce7. Login renders in-chat (Commerce7); Register still links
  // out. See account.js's commerce7 branch for the BE calls.
  // if (isTrue(getFeature("commerce7")) && getUiFeature("show_user_account", false)) {
  //   registerMessageKind("login-form", PVLoginForm);
  //   accountFeature.init();
  // }
  if (isTrue(getFeature("commerce7")) && getUiFeature("show_user_account", false)) {
      registerMessageKind("login-form", PVLoginForm);
      accountFeature.init();
    }
}
